import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { IonCard, IonCardContent } from "@ionic/react";
import Avatar from "../avatar/Avatar"; // Adjust import path if needed
import { getHashtagMembers } from "../../api/requests/hashtag";
import "./CommunityMembers.css";
const membersCache = new Map();
const CommunityMembersSkeleton = () => (
  <IonCard className="community-members-card">
    <IonCardContent className="community-members-content">
      <div className="community-members-header">
        <span className="community-members-title">Members</span>
      </div>

      <div className="community-members-list">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="community-member-item">
            <div className="member-skeleton-avatar" />
            <div className="member-skeleton-name" />
          </div>
        ))}
      </div>
    </IonCardContent>
  </IonCard>
);

const MEMBERS_LIMIT = 20;

const CommunityMembers = ({ tag }) => {
  const cachedMembers = membersCache.get(tag);

  const [members, setMembers] = useState(cachedMembers?.members || []);
  const [page, setPage] = useState(cachedMembers?.page || 1);
  const [hasMore, setHasMore] = useState(cachedMembers?.hasMore ?? true);
  const [initialLoading, setInitialLoading] = useState(!cachedMembers);
  const [loadingMore, setLoadingMore] = useState(false);
  const listRef = useRef(null);

  const fetchMembers = useCallback(
    async (pageToFetch = 1) => {
      if (!tag) return;

      if (pageToFetch === 1) {
        if (!membersCache.has(tag)) {
          setInitialLoading(true);
        }
      } else {
        setLoadingMore(true);
      }
      try {
        const response = await getHashtagMembers(
          tag,
          pageToFetch,
          MEMBERS_LIMIT,
        );

        if (response?.status === 200) {
          const newMembers = response.data.members || [];

          setMembers((prev) =>
            pageToFetch === 1 ? newMembers : [...prev, ...newMembers],
          );
          const updatedMembers =
            pageToFetch === 1
              ? newMembers
              : [...(membersCache.get(tag)?.members || []), ...newMembers];

          membersCache.set(tag, {
            members: updatedMembers,
            page: pageToFetch,
            hasMore: response.data.hasMore,
          });
          setHasMore(response.data.hasMore);
          setPage(pageToFetch);
        }
      } finally {
        setInitialLoading(false);
        setLoadingMore(false);
      }
    },
    [tag],
  );

  useEffect(() => {
    const cached = membersCache.get(tag);

    if (cached) {
      setMembers(cached.members);
      setPage(cached.page);
      setHasMore(cached.hasMore);
      setInitialLoading(false);
      return;
    }

    setMembers([]);
    setPage(1);
    setHasMore(true);
    setInitialLoading(true);
    fetchMembers(1);
  }, [tag, fetchMembers]);

  const handleMembersScroll = () => {
    const el = listRef.current;
    if (!el || loadingMore || !hasMore) return;

    const nearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 80;

    if (nearEnd) {
      fetchMembers(page + 1);
    }
  };
  if (initialLoading) return <CommunityMembersSkeleton />;
  if (!members || members.length === 0) {
    return null;
  }

  return (
    <IonCard className="community-members-card">
      <IonCardContent className="community-members-content">
        <div className="community-members-header">
          <span className="community-members-title">Members</span>
        </div>
        <div
          className="community-members-list"
          ref={listRef}
          onScroll={handleMembersScroll}
        >
          {members.map((member) => (
            <div key={member._id} className="community-member-item">
              <Avatar
                username={member.username}
                avatar={member.avatar}
                province={member.province}
                size="md"
                isAdmin={member.isCommunityAdmin}
                isOfficial={member.isAdmin}
              />
              <Link
                to={`/profile/${member.username}`}
                className={`member-name ${member.isAdmin ? "text-admin" : ""}`}
              >
                <span>{member.fname || member.username}</span>
              </Link>
            </div>
          ))}
        </div>
      </IonCardContent>
    </IonCard>
  );
};

export default CommunityMembers;
